import React, { useContext, useEffect } from 'react';
import { graphql } from 'gatsby';

// Components
import {
  Layout,
  NetworkBanner,
  MediaCollection,
  SEO,
  ShopBannerCTA,
} from '../../components';
import { Heading } from '../../components/a11y';

// Context
import { AnalyticsContext } from '../../analytics/AnalyticsContext';

// Utils
import { seoTitleTemplates } from '../../utils/networkMap';
import { noSpaces, getBuyflowLink } from '../../utils/helpers';
import { buyflowUrlMap } from '../../utils/buyflowUrlMap';

// Interfaces
import { IMedia, INetwork } from '../../utils/models';

// hooks
import { useVisitId } from '../../hooks/useVisitId';
import useContextToken from '../../hooks/useContextToken';

// Styles
import './network.scss';

// TEALIUM
import initTealiumTracker from 'tealium-tracker';

let tealiumTrackPageLoad;
if (typeof window !== 'undefined') {
  const { trackPageLoad } = initTealiumTracker({ debugMode: true });
  tealiumTrackPageLoad = trackPageLoad;
}

interface IProps {
  data: any;
  pageContext: {
    network: INetwork;
  };
}

export const NETWORK_QUERY_ALL = graphql`
  query (
    $providerId: String!
    $providerNames: [String!]
    $displayName: String!
  ) {
    allSOD {
      networkAssets(
        providerId: $providerId
        providerNames: $providerNames
        cacheValue: false
      ) {
        items {
          assetType
          id
          altIds {
            tmsRootId
            tmsSeriesId
          }
          title
          description
          imageUrl
          offer {
            offerType
            providerId
            providerName
          }
        }
      }
    }

    allStrapi {
      networks(filters: { name: { eq: $displayName } }) {
        documentId
        tmsId
        badges {
          id
          url
          live_tv_app {
            width
            height
            image {
              url
            }
          }
        }
      }
    }
  }
`;

const NetworkDefault = ({ data, pageContext }: IProps) => {
  const { analytics } = useContext(AnalyticsContext);

  const { items } = data.allSOD.networkAssets;

  const tabs = ['All', 'Movies', 'TV Shows'];

  const { network } = pageContext;
  const { displayName, description, seoTitleFormat } = network;

  // Tv App badges and live tv tmsId fetched from cms
  const badges = data?.allStrapi?.networks?.[0]?.badges;
  const ovpTmsId = data?.allStrapi?.networks?.[0]?.tmsId;

  const visitId = useVisitId();
  const { contextToken } = useContextToken();

  let [unauthLink, authLink] = ['', ''];
  if (displayName in buyflowUrlMap) {
    [unauthLink, authLink] = buyflowUrlMap[displayName];
  } else {
    [unauthLink, authLink] = buyflowUrlMap.default;
  }

  const buyflowLink = getBuyflowLink(unauthLink, authLink);

  // Quantum Tracking for page view
  useEffect(() => {
    if (analytics) {
      analytics.trackPageView(`Network - ${displayName}`, {
        currPageAppSection: 'Network',
        currPageIsLazyLoad: false,
      });
    }
  }, [analytics, displayName]);

  // Tealium Page Tracking
  useEffect(() => {
    tealiumTrackPageLoad({
      pagename: 'Network Details Page',
      section: 'TV',
      pagetype: 'Network Page',
      sc_reporting_suite: 'charterspectrumondemand',
    });
  }, []);

  return (
    <div className="network-page-wrapper">
      <Layout
        className="network-page"
        header={
          <NetworkBanner network={network} tmsId={ovpTmsId} badges={badges} />
        }
      >
        <SEO
          title={`${seoTitleTemplates[seoTitleFormat]} ${displayName}`}
          description={description}
        />

        <Heading className="visually-hidden">{displayName}</Heading>

        <ShopBannerCTA
          title="Watch this Network with Spectrum TV"
          linkText="Shop Plans"
          linkPath={`${buyflowLink}&visitId=${visitId}${contextToken}`}
        />

        <MediaCollection
          tabs={tabs}
          items={items as IMedia[]}
          networkName={noSpaces(displayName).toLowerCase()}
          showTitle={true}
        />
      </Layout>
    </div>
  );
};

export default NetworkDefault;
